import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../../i18n/useTranslation';
import { booksellersManageService } from '../../../api/booksellersManageService';
import type { BooksellerManageDto } from '../../../types/manageBooksellers';

interface Props {
  bookseller: BooksellerManageDto | null;
  onClose: () => void;
}

export default function BooksellerDeleteConfirmDialog({ bookseller, onClose }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const del = useMutation({
    mutationFn: () => booksellersManageService.delete(bookseller!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['manage-booksellers'] });
      onClose();
    },
    onError: (err) => {
      const e = err as { response?: { data?: { error?: { message?: string } } }; message?: string };
      setErrorMessage(e?.response?.data?.error?.message ?? e?.message ?? 'Failed to delete bookseller');
    },
  });

  if (!bookseller) return null;

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-5"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="delete-bookseller-title"
      >
        <h2 id="delete-bookseller-title" className="text-lg font-semibold mb-2">{t('App.Admin.Booksellers.BooksellerDeleteConfirmDialog.Title')}</h2>
        <p className="text-sm text-neutral-700 mb-4">
          {t('App.Admin.Booksellers.BooksellerDeleteConfirmDialog.Description', { name: bookseller.name, slug: bookseller.slug })}
        </p>

        {errorMessage && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded px-3 py-2 mb-3">
            {errorMessage}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1 border border-neutral-300 rounded text-sm hover:bg-neutral-50"
            disabled={del.isPending}
          >
            {t('App.Admin.Booksellers.BooksellerDeleteConfirmDialog.Cancel')}
          </button>
          <button
            type="button"
            onClick={() => del.mutate()}
            disabled={del.isPending}
            className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700 disabled:opacity-50"
          >
            {del.isPending ? t('App.Admin.Booksellers.BooksellerDeleteConfirmDialog.Deleting') : t('App.Admin.Booksellers.BooksellerDeleteConfirmDialog.Delete')}
          </button>
        </div>
      </div>
    </div>
  );
}
